import { FormikProvider, useFormik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import FormError from "../../Tags/FormError";
import { addMembersToTeam } from "../../../apis/workspace/teams";
import { toast } from "react-hot-toast";
import { useMutation, useQueryClient } from "react-query";
import Image from "../../Tags/Image";
import { Button } from "../../ui/button";
import UserMultiSelect from "../shared/UserMultiSelect";
import { useCheckTeamMembersConflicts } from "../../../apiHooks/useCheckTeamMembersConflicts";

const areArraysEqual = (a = [], b = []) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

export default function AddMembersModal({
  closeModal,
  data = {},
  teamMembers = [],
  activeWorkspace,
  refetch = () => undefined,
}) {
  const queryClient = useQueryClient();
  const workspaceId = activeWorkspace?.id;

  const getIds = (list) => {
    if (!Array.isArray(list)) return [];
    return list.map((item) => item && item.id).filter(Boolean);
  };

  const existingMemberIds = useMemo(() => getIds(data?.members), [data?.members]);
  const availableMembers = useMemo(() => {
    if (!Array.isArray(teamMembers)) return [];
    return teamMembers.filter((member) => member && !existingMemberIds.includes(member.id));
  }, [teamMembers, existingMemberIds]);

  const [selectedIds, setSelectedIds] = useState([]);
  const [conflictMembers, setConflictMembers] = useState({});
  const [hasConflicts, setHasConflicts] = useState(false);
  const conflictCheckRequestId = useRef(0);

  const { mutateAsync: checkTeamMemberConflicts, isLoading: isCheckingTeamMemberConflicts } =
    useCheckTeamMembersConflicts({
      onError: (error) => {
        const message = error?.response?.data?.message ?? "Failed to check member conflicts.";
        toast.error(message);
      },
    });

  const runConflictCheck = async (memberIds = []) => {
    const safeMemberIds = Array.isArray(memberIds) ? memberIds : [];
    conflictCheckRequestId.current += 1;
    const requestId = conflictCheckRequestId.current;

    if (!workspaceId || !data?.id || safeMemberIds.length === 0) {
      setConflictMembers({});
      setHasConflicts(false);
      return;
    }

    try {
      const response = await checkTeamMemberConflicts({
        workspaceId,
        teamId: data?.id,
        teamMembers: safeMemberIds,
      });

      if (conflictCheckRequestId.current !== requestId) {
        return;
      }

      const conflicts =
        response?.data?.conflicts ??
        response?.data?.data?.conflicts ??
        response?.conflicts ??
        response?.data ??
        [];

      const conflictEntries = Array.isArray(conflicts)
        ? conflicts
            .map((conflict) => {
              const memberId =
                conflict?.user?.id ||
                conflict?.team_member?.id ||
                conflict?.member?.id ||
                conflict?.member_id ||
                conflict?.team_member_id ||
                conflict?.id;
              const teamName = conflict?.current_team?.name || conflict?.team?.name || "another";
              if (typeof memberId === "string" && memberId.length > 0) {
                return [memberId, teamName];
              }
              return null;
            })
            .filter(Boolean)
        : [];

      const conflictMap = Object.fromEntries(conflictEntries);
      setConflictMembers(conflictMap);
      setHasConflicts(conflictEntries.length > 0);
    } catch (error) {
      if (conflictCheckRequestId.current === requestId) {
        setConflictMembers({});
        setHasConflicts(false);
      }
    }
  };

  useEffect(() => {
    setSelectedIds([]);
    setConflictMembers({});
    setHasConflicts(false);
    conflictCheckRequestId.current += 1;
  }, [data?.id]);

  const formInitialValues = useMemo(() => ({ team_members: [] }), []);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        team_members: Yup.array()
          .of(Yup.string().required())
          .min(1, "Select at least one member")
          .required("Select at least one member"),
      }),
    []
  );

  const handleModalClose = () => {
    formik.resetForm();
    setSelectedIds([]);
    setConflictMembers({});
    setHasConflicts(false);
    conflictCheckRequestId.current += 1;
    closeModal();
  };

  const formik = useFormik({
    initialValues: formInitialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      const payload = { team_members: values.team_members };
      addMembersToTeamFn.mutate({
        data: payload,
        teamId: data?.id,
        options: {
          headers: { "X-Workspace-Id": workspaceId },
        },
      });
    },
  });

  useEffect(() => {
    const current = formik.values?.team_members || [];
    if (!areArraysEqual(current, selectedIds)) {
      formik.setFieldValue("team_members", selectedIds, true);
    }
  }, [formik, selectedIds]);

  const handleMembersChange = (ids = []) => {
    if (!Array.isArray(ids)) return;
    const uniqueIds = [...new Set(ids)];
    setSelectedIds(uniqueIds);
    runConflictCheck(uniqueIds);
  };

  const addMembersToTeamFn = useMutation("add_team_members_to_team", addMembersToTeam, {
    onSuccess(response) {
      toast.success(response?.message);
      handleModalClose();
      queryClient.invalidateQueries("view_team");
      if (typeof refetch === "function") {
        refetch();
      }
      formik.setSubmitting(false);
    },
    onError() {
      formik.setSubmitting(false);
    },
  });

  return (
    <>
      <div className="mb-1 inline-flex space-x-3">
        <div className="inline-flex justify-center border border-border rounded-[--radius] p-2 bg-background w-[50px]">
          <Image src="/images/icons/untitledui/group.svg" width="20" className="inline-block" />
        </div>
        <div>
          <h2 className="text-base font-semibold leading-7 ">Add team members</h2>
          <p className="text-paragraph">Please select members to add in this team.</p>
        </div>
      </div>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <div className="py-3">
            <div className="mb-5">
              <label className="block mb-2 text-sm font-medium leading-6">Members</label>
              <div>
                <UserMultiSelect
                  users={availableMembers}
                  selectedIds={selectedIds}
                  onChange={handleMembersChange}
                  disabled={addMembersToTeamFn.isLoading}
                  loading={false}
                  warningIds={Object.keys(conflictMembers)}
                  warningDetails={conflictMembers}
                  error={false}
                  placeholder="Select users"
                  emptyMessage="No users found"
                  errorMessage="Error loading users"
                />
                {/* {isCheckingTeamMemberConflicts && (
                  <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
                    Checking selected members for conflicts...
                  </div>
                )} */}
                {hasConflicts && (
                  <div className="mt-2  text-warning-700">
                    Some users are part of other teams. Verify before proceeding.
                  </div>
                )}
                <FormError field="team_members" />
              </div>
            </div>
          </div>
          <div className="mt-0 space-x-2 flex">
            <Button
              className="flex-1"
              onClick={(e) => {
                e.preventDefault();
                handleModalClose();
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={formik.isSubmitting || !formik.isValid} className="flex-1">
              Add team members
              {formik.isSubmitting ? <div className="loader_circle ml-3"></div> : ""}
            </Button>
          </div>
        </form>
      </FormikProvider>
    </>
  );
}
